import React, { useState } from "react";
import { toast } from "sonner";
import { Paperclip, Upload, X, File as FileIcon } from "lucide-react";

/**
 * Staged-file attachment control used on CREATE forms (Voucher / Invoice /
 * Bill / PO / SO / Quote / PR / GRN). Files are held in local state; after the
 * parent saves the document and gets a doc_id back, it calls `flush(docType, docId)`
 * to POST every file to /api/attachments/{doc_type}/{doc_id}.
 *
 * Usage:
 *   const stagedRef = useRef();
 *   <StagedAttachments ref={stagedRef} />
 *   ...
 *   const inv = await api.post("/sales/invoices", form);
 *   await stagedRef.current.flush("sales_invoice", inv.data.id);
 */
const StagedAttachments = React.forwardRef(({ label = "Attach supporting files", docType }, ref) => {
  const [files, setFiles] = useState([]);

  React.useImperativeHandle(ref, () => ({
    files: () => files,
    clear: () => setFiles([]),
    flush: async (dt, docId) => {
      const useDt = dt || docType;
      if (!docId || files.length === 0) return { uploaded: 0 };
      const { default: api } = await import("@/lib/api");
      let ok = 0, fail = 0;
      for (const f of files) {
        try {
          const fd = new FormData(); fd.append("file", f);
          await api.post(`/attachments/${useDt}/${docId}`, fd);
          ok++;
        } catch { fail++; }
      }
      if (ok) toast.success(`${ok} file(s) attached`);
      if (fail) toast.error(`${fail} file(s) failed to upload`);
      setFiles([]);
      return { uploaded: ok, failed: fail };
    },
  }));

  const onPick = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length === 0) return;
    // Client-side 8 MB cap per file (matches backend)
    const valid = picked.filter((f) => {
      if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name} > 8 MB — skipped`); return false; }
      return true;
    });
    setFiles((cur) => [...cur, ...valid]);
    e.target.value = "";
  };

  const remove = (idx) => setFiles((cur) => cur.filter((_, i) => i !== idx));

  return (
    <div className="border rounded-sm p-3 space-y-2" data-testid="staged-attach">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1">
          <Paperclip className="w-3 h-3" /> {label} {files.length > 0 && `(${files.length})`}
        </div>
        <label className="cursor-pointer">
          <input type="file" multiple onChange={onPick} className="hidden" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt" data-testid="staged-attach-input" />
          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 border rounded-sm hover:bg-muted">
            <Upload className="w-3.5 h-3.5" /> Add files
          </span>
        </label>
      </div>
      {files.length === 0
        ? <div className="text-xs text-muted-foreground italic py-1">No files yet — pick supporting documents (PDF, image, sheet). Uploaded automatically when you save.</div>
        : (
          <div className="space-y-1">
            {files.map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-xs border rounded-sm px-2 py-1.5">
                <FileIcon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <span className="flex-1 truncate">{f.name}</span>
                <span className="text-[10px] font-mono text-muted-foreground">{Math.ceil(f.size / 1024)} KB</span>
                <button type="button" onClick={() => remove(i)} className="text-muted-foreground hover:text-destructive" data-testid={`staged-remove-${i}`}><X className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
        )}
    </div>
  );
});

StagedAttachments.displayName = "StagedAttachments";
export default StagedAttachments;
